import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, X, CalendarDays } from "lucide-react";
import { updateAdminBooking, fetchVehicles } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDMY } from "@/lib/date";
import { TimeField } from "@/components/TimeField";

const isoToDate = (iso) => (iso ? iso.slice(0, 10) : "");
const isoToTime = (iso) => (iso ? iso.slice(11, 16) : "");
const toDateObj = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const UpdateBookingDialog = ({ booking, onClose, onSaved }) => {
  const [vehicleOptions, setVehicleOptions] = useState([booking.vehicle]);
  useEffect(() => {
    fetchVehicles()
      .then((vs) => {
        const names = vs.map((v) => v.name);
        setVehicleOptions(names.includes(booking.vehicle) ? names : [booking.vehicle, ...names]);
      })
      .catch(() => {});
  }, [booking.vehicle]);
  const [bDate, setBDate] = useState(booking.booking_date);
  const [bDateOpen, setBDateOpen] = useState(false);
  const [startT, setStartT] = useState(booking.start_time);
  const [returnT, setReturnT] = useState(booking.return_time);
  const [vehicle, setVehicle] = useState(booking.vehicle);
  const [artDate, setArtDate] = useState(isoToDate(booking.actual_return_datetime));
  const [artTime, setArtTime] = useState(isoToTime(booking.actual_return_datetime));
  const [dateOpen, setDateOpen] = useState(false);
  const [battery, setBattery] = useState(
    booking.current_battery_percentage != null ? String(booking.current_battery_percentage) : "",
  );
  const [condition, setCondition] = useState(booking.return_condition || "");
  const [remarks, setRemarks] = useState(booking.return_remarks || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (battery !== "") {
      const b = Number(battery);
      if (!Number.isInteger(b) || b < 0 || b > 100) {
        toast.error("Battery must be a whole number from 0 to 100");
        return;
      }
    }
    let art;
    if (artDate && artTime) {
      art = `${artDate}T${artTime}`;
    } else if (artDate || artTime) {
      toast.error("Please provide both actual return date and time");
      return;
    }
    const payload = {
      return_condition: condition,
      return_remarks: remarks,
      booking_date: bDate,
      start_time: startT,
      return_time: returnT,
      vehicle,
    };
    if (art) payload.actual_return_datetime = art;
    if (battery !== "") payload.current_battery_percentage = Number(battery);

    setSaving(true);
    try {
      await updateAdminBooking(booking.booking_ref, payload);
      toast.success(`Booking ${booking.booking_ref} updated`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition-all focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";
  const labelCls = "mb-1 block text-xs font-semibold text-[#0F172A]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="update-dialog">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-[#0F172A]">
            Edit {booking.booking_ref}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="update-dialog-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 text-xs text-[#64748B]">
          {booking.vehicle} &middot; {booking.employee_name}
        </p>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Vehicle</label>
            <select value={vehicle} onChange={(e) => setVehicle(e.target.value)} className={inputCls} data-testid="edit-vehicle">
              {vehicleOptions.map((v) => (<option key={v} value={v}>{v}</option>))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Booking date</label>
            <Popover open={bDateOpen} onOpenChange={setBDateOpen}>
              <PopoverTrigger asChild>
                <button type="button" className={`${inputCls} flex items-center gap-2`} data-testid="edit-booking-date">
                  <CalendarDays className="h-4 w-4 shrink-0 text-[#5C2D91]" />
                  {bDate ? formatDMY(bDate) : "dd/mm/yyyy"}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="z-[60] w-auto rounded-xl p-0">
                <Calendar
                  mode="single"
                  selected={toDateObj(bDate)}
                  onSelect={(d) => { if (d) { setBDate(toDateStr(d)); setBDateOpen(false); } }}
                  classNames={{
                    day_selected: "bg-[#5C2D91] text-white hover:bg-[#4A2475] hover:text-white rounded-md",
                    day_today: "ring-2 ring-[#5C2D91] ring-offset-1 rounded-md font-bold text-[#5C2D91]",
                    day: "h-9 w-9 p-0 font-normal rounded-md text-[#0F172A] hover:bg-[#5C2D91]/10",
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start time</label>
              <TimeField value={startT} onChange={setStartT} testid="edit-start-time" />
            </div>
            <div>
              <label className={labelCls}>Estimated return time</label>
              <TimeField value={returnT} onChange={setReturnT} testid="edit-return-time" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Actual return date &amp; time</label>
            <div className="flex gap-2">
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`${inputCls} flex flex-1 items-center gap-2`}
                    data-testid="update-actual-return-date"
                  >
                    <CalendarDays className="h-4 w-4 shrink-0 text-[#5C2D91]" />
                    {artDate ? formatDMY(artDate) : "dd/mm/yyyy"}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="z-[60] w-auto rounded-xl p-0">
                  <Calendar
                    mode="single"
                    selected={toDateObj(artDate)}
                    onSelect={(d) => { if (d) { setArtDate(toDateStr(d)); setDateOpen(false); } }}
                    classNames={{
                      day_selected: "bg-[#5C2D91] text-white hover:bg-[#4A2475] hover:text-white rounded-md",
                      day_today: "ring-2 ring-[#5C2D91] ring-offset-1 rounded-md font-bold text-[#5C2D91]",
                      day: "h-9 w-9 p-0 font-normal rounded-md text-[#0F172A] hover:bg-[#5C2D91]/10",
                    }}
                  />
                  <div className="border-t border-slate-100 p-2">
                    <button
                      onClick={() => { setArtDate(""); setDateOpen(false); }}
                      className="w-full rounded-md px-3 py-1.5 text-sm font-medium text-[#64748B] hover:bg-slate-50"
                      data-testid="update-actual-return-date-clear"
                    >
                      Clear
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <TimeField value={artTime} onChange={setArtTime} className="w-36 shrink-0" testid="update-actual-return-time" />
            </div>
            <p className="mt-1 text-[11px] text-[#94a3b8]">
              Leave blank to keep the vehicle unreturned. Cannot be earlier than the start time.
            </p>
          </div>

          <div>
            <label className={labelCls}>Current battery after ride (%)</label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={battery}
                onChange={(e) => setBattery(e.target.value)}
                placeholder="0–100"
                className={`${inputCls} pr-8`}
                data-testid="update-battery"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#64748B]">%</span>
            </div>
          </div>

          <div>
            <label className={labelCls}>Return condition (optional)</label>
            <input
              type="text"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder="e.g. Good, minor scratch"
              className={inputCls}
              data-testid="update-condition"
            />
          </div>

          <div>
            <label className={labelCls}>Return remarks (optional)</label>
            <textarea
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any notes"
              className={`${inputCls} resize-none`}
              data-testid="update-remarks"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0F172A] transition-all hover:bg-slate-50"
            data-testid="update-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] active:scale-[0.98] disabled:opacity-60"
            data-testid="update-save"
          >
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};
